import React, { memo, useCallback, useMemo } from "react";
import { Icon } from "uikit";

import { OrderFormProps } from "../../types/FormProps";
import { GridSelectMemo } from "../GridSelect";

import { paymentMethodsIds, paymentColors } from "./paymentMethods";
import filterPaymentMethods from "./filterPaymentMethods";

const GridItemPayment: React.FC<GridItemPayment.Props> = ({
	form,
	disabled,
	area,
	tab,
}) => {
	const paymentStatus = useMemo(
		() => tab.form.paymentStatus?.status || null,
		[tab.form.paymentStatus],
	);

	const paymentType = useMemo(
		() => tab.form.paymentType || null,
		[tab.form.paymentType],
	);

	const styles = useMemo(() => {
		const statusColor =
			paymentType !== "company_bill"
				? paymentColors.find((item) => item.id === paymentStatus)?.color
				: undefined;
		const hasStatusColor = Boolean(statusColor) && statusColor !== "#F6F6F6";

		return {
			elem: {
				justifySelf: "start",
				alignSelf: "center",
				bgC: "#F6F6F6",
			},
			wrap: {
				justify: "center",
				alignItems: "center",
				h: "28px",
				p: "0 0 0 10px",
				bgC: statusColor || "#F6F6F6",
			},
			// Status colours (green/red/blue) are dark: keep the value readable,
			// including the disabled (closed order) state, same as the table cell.
			textColor: hasStatusColor ? "#FFFFFF" : undefined,
			icon: {
				w: "16px",
				h: "16px",
				colors: hasStatusColor ? "#FFFFFF" : "#5e6b73",
			},
		};
	}, [paymentStatus, paymentType]);

	const handleSave = useCallback(() => {
		form.setValue("orderSave", true);
		form.setValue("orderPointSave", false);
	}, [form]);

	const onSelect = useCallback(
		(option) => {
			if (paymentMethodsIds.includes(option.value)) {
				form.setValue("paymentType", option.value);

				form.setValue("paymentMethod", {
					name: option.value,
					id: paymentMethodsIds.indexOf(option.value),
				});
				handleSave();
			}
		},
		[form, handleSave],
	);

	const dataOptions = useMemo(() => {
		const type = tab.form.source;
		const paymentType = tab.form?.paymentType;
		const paymentStatus = tab.form?.paymentStatus?.status || null;
		const checkCardId = tab.form?.counterparty?.checkCardId;
		const paymentAccountId = tab.form?.counterparty?.paymentAccountId;

		return {
			type,
			checkCardId,
			paymentAccountId,
			paymentType,
			paymentStatus,
		};
	}, [
		tab.form.source,
		tab.form?.paymentType,
		tab.form?.paymentStatus?.status,
		tab.form?.counterparty?.checkCardId,
		tab.form?.counterparty?.paymentAccountId,
	]);

	const options = useMemo(
		() => filterPaymentMethods(dataOptions),
		[dataOptions],
	);

	return (
		<GridSelectMemo
			area={area || "payment"}
			disabled={disabled}
			name="paymentType"
			control={form.control}
			options={options}
			onSelect={onSelect}
			icon={<Icon id="order-modal-payment" size={16} />}
			styles={styles}
		/>
	);
};

declare namespace GridItemPayment {
	interface Props extends OrderFormProps {
		area?: string;
	}
}
export const GridItemPaymentMemo = memo(GridItemPayment);
export default GridItemPayment;
